/**
 * LandingFounderApps Component
 *
 * Cards showcasing the founder tools: Idea Finder, Founder Type and
 * Zeitgeist. Used on both the logged-out marketing landing page and the
 * logged-in `/founder-apps` route. The latter renders its own page-level
 * `<h1>` + intro, so it passes `showHeader={false}` to suppress the section's
 * `<h2>` and avoid two "Founder Apps" headers on the same page (LEV-700
 * follow-up).
 *
 * The row is `flex-1 min-w-[280px]` per card, so it sizes to the number of
 * entries: three across on desktop, stacking on mobile. Adding a fourth needs
 * no layout change either.
 */
"use client";

import Image from "next/image";
import { ArrowUpRight } from "lucide-react";
import { AnimatedSection } from "@/components/atoms/AnimatedSection";
import { trackFounderAppClicked } from "@/lib/analytics/client";
import { FOUNDER_APPS, type FounderApp } from "@/constants/founderApps";

function FounderAppCard({ app, index }: { app: FounderApp; index: number }) {
  return (
    <AnimatedSection delay={index * 0.1} className="flex-1 min-w-[280px]">
      <div className="bg-white rounded-2xl p-6 h-full flex flex-col">
        <div className="relative w-12 h-12 rounded-xl overflow-hidden mb-4 bg-landing-bg-medium">
          <Image src={app.icon} alt={app.title} fill className="object-cover" sizes="48px" />
        </div>

        <div className="bg-landing-bg-medium inline-block px-2 py-1 rounded mb-3">
          <span className="font-mono text-xs text-landing-secondary uppercase">{app.tag}</span>
        </div>

        <h3 className="text-xl text-landing-dark font-medium mb-3">{app.title}</h3>

        <p className="text-landing-secondary text-sm leading-relaxed mb-4">{app.description}</p>

        <a
          href={app.href}
          onClick={() => trackFounderAppClicked({ appName: app.title, location: "card" })}
          className="mt-auto inline-flex items-center gap-1 self-start text-lev-orange text-sm font-medium hover:underline"
        >
          Launch App
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </AnimatedSection>
  );
}

export interface LandingFounderAppsProps {
  /**
   * Render the section's own "Founder Apps" `<h2>`. Defaults to `true` for
   * the marketing landing page; the logged-in `/founder-apps` route passes
   * `false` because it already renders a page-level `<h1>` + intro.
   */
  showHeader?: boolean;
}

export function LandingFounderApps({ showHeader = true }: LandingFounderAppsProps = {}) {
  return (
    // overflow-x-clip: the cards slide in (AnimatedSection), and before they
    // reveal their offset widens the page (36 px at 1280).
    <section id="founder-apps" className="overflow-x-clip bg-landing-bg-medium px-4 py-20">
      <div className="w-full max-w-[1032px] mx-auto">
        {showHeader && (
          <AnimatedSection className="mb-12">
            <h2 className="text-[28px] md:text-[36px] text-landing-dark tracking-tight">
              Founder Apps
            </h2>
          </AnimatedSection>
        )}

        <div className="flex flex-col md:flex-row gap-6">
          {FOUNDER_APPS.map((app, index) => (
            <FounderAppCard key={app.title} app={app} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
